import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { groupService } from '../../services/group.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { toastService } from '../../services/toast.service'
import { userService } from '../../services/user.service'
import { Group, MailAccount, MailRule, User } from '../../types'
import { InputSelect } from '../common/InputSelect'
import { InputSwitch } from '../common/InputSwitch'
import { InputTags } from '../common/InputTags'

interface PermissionsDialogProps {
  show: boolean
  onHide: () => void
  object: MailAccount | MailRule | null
  onSave: (permissions: any) => void
  readOnly?: boolean
}

interface PermissionsData {
  owner: number | null
  set_permissions: {
    view: {
      users: number[]
      groups: number[]
    }
    change: {
      users: number[]
      groups: number[]
    }
  }
}

const PermissionsDialog: React.FC<PermissionsDialogProps> = ({ show, onHide, object, onSave, readOnly = false }) => {
  const { t } = useTranslation()
  const [users, setUsers] = useState<User[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [merge, setMerge] = useState(true)

  const [permissions, setPermissions] = useState<PermissionsData>({
    owner: null,
    set_permissions: {
      view: {
        users: [],
        groups: [],
      },
      change: {
        users: [],
        groups: [],
      },
    },
  })

  useEffect(() => {
    if (show) {
      loadData()
    }
  }, [show])

  useEffect(() => {
    if (object && show) {
      // Backend može vratiti permissions ili set_permissions
      const perms = object.set_permissions ||
        object.permissions || {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        }

      setPermissions({
        owner: object.owner || null,
        set_permissions: perms,
      })
    }
  }, [object, show])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [usersRes, groupsRes] = await Promise.all([
        permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)
          ? userService.listAll({ full_perms: true })
          : Promise.resolve({ results: [] as User[] }),
        permissionsService.currentUserCan(PermissionAction.View, PermissionType.Group)
          ? groupService.listAll({ full_perms: true })
          : Promise.resolve({ results: [] as Group[] }),
      ])
      setUsers(usersRes.results)
      setGroups(groupsRes.results)
    } catch (err) {
      setError('Failed to load users and groups')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!object) return
    if (readOnly) return

    setSaving(true)
    try {
      const permissionsData = {
        owner: permissions.owner,
        set_permissions: permissions.set_permissions,
      }

      await onSave(permissionsData)
      toastService.showSuccess(t('permissions_updated'))
      onHide()
    } catch (err) {
      toastService.showError(t('error_updating_permissions'))
    } finally {
      setSaving(false)
    }
  }

  const handleOwnerChange = (value: number | null) => {
    setPermissions((prev) => ({
      ...prev,
      owner: value,
    }))
  }

  const handleViewUsersChange = (value: number[]) => {
    setPermissions((prev) => ({
      ...prev,
      set_permissions: {
        ...prev.set_permissions,
        view: {
          ...prev.set_permissions.view,
          users: value,
        },
      },
    }))
  }

  const handleViewGroupsChange = (value: number[]) => {
    setPermissions((prev) => ({
      ...prev,
      set_permissions: {
        ...prev.set_permissions,
        view: {
          ...prev.set_permissions.view,
          groups: value,
        },
      },
    }))
  }

  const handleChangeUsersChange = (value: number[]) => {
    setPermissions((prev) => ({
      ...prev,
      set_permissions: {
        ...prev.set_permissions,
        change: {
          ...prev.set_permissions.change,
          users: value,
        },
      },
    }))
  }

  const handleChangeGroupsChange = (value: number[]) => {
    setPermissions((prev) => ({
      ...prev,
      set_permissions: {
        ...prev.set_permissions,
        change: {
          ...prev.set_permissions.change,
          groups: value,
        },
      },
    }))
  }

  const getTitle = () => {
    if (!object) return t('edit_permissions')
    return t('edit_permissions_for', { name: object.name })
  }

  const userOptions = users.map((user) => ({
    value: user.id,
    label: user.username,
  }))

  const groupOptions = groups.map((group) => ({
    value: group.id,
    label: group.name,
  }))

  return (
    <Modal show={show} onHide={onHide} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{getTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <Form>
            <fieldset
              disabled={readOnly}
              aria-disabled={readOnly}
              className="border-0 p-0 m-0"
              style={readOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}
            >
            <div className="form-group">
              <h5>{t('permissions')}</h5>

              <Row className="mb-3">
                <Col lg={9} className="offset-lg-3">
                  <InputSelect
                    title={t('owner')}
                    value={permissions.owner}
                    onChange={(value) => handleOwnerChange(value)}
                    items={[{ id: null, name: t('no_owner') }, ...userOptions.map((option) => ({ id: option.value, name: option.label }))]}
                    bindLabel="name"
                    allowNull={true}
                    hint={t('objects_without_owner_can_be_viewed_by_all')}
                  />
                </Col>
              </Row>

              <div className="mb-4">
                <h6>{t('view')}</h6>

                <Row className="mb-2">
                  <Col lg={3}>
                    <Form.Label>{t('users')}:</Form.Label>
                  </Col>
                  <Col lg={9}>
                    <InputTags
                      title=""
                      value={permissions.set_permissions.view.users}
                      onChange={handleViewUsersChange}
                      multiple={true}
                      options={userOptions}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col lg={3}>
                    <Form.Label>{t('groups')}:</Form.Label>
                  </Col>
                  <Col lg={9}>
                    <InputTags
                      title=""
                      value={permissions.set_permissions.view.groups}
                      onChange={handleViewGroupsChange}
                      multiple={true}
                      options={groupOptions}
                    />
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6>{t('edit')}</h6>

                <Row className="mb-2">
                  <Col lg={3}>
                    <Form.Label>{t('users')}:</Form.Label>
                  </Col>
                  <Col lg={9}>
                    <InputTags
                      title=""
                      value={permissions.set_permissions.change.users}
                      onChange={handleChangeUsersChange}
                      multiple={true}
                      options={userOptions}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col lg={3}>
                    <Form.Label>{t('groups')}:</Form.Label>
                  </Col>
                  <Col lg={9}>
                    <InputTags
                      title=""
                      value={permissions.set_permissions.change.groups}
                      onChange={handleChangeGroupsChange}
                      multiple={true}
                      options={groupOptions}
                    />
                  </Col>
                </Row>
                <small className="form-text text-muted">{t('edit_permissions_also_grant_view_permissions')}</small>
              </div>

              <Row className="mt-4">
                <Col lg={9} className="offset-lg-3">
                  <InputSwitch title={t('merge_with_existing_permissions')} value={merge} onChange={(value) => setMerge(value)} horizontal={true} />
                </Col>
              </Row>
            </div>
            </fieldset>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        {saving && (
          <>
            <Spinner size="sm" className="me-2" />
            <span className="visually-hidden">{t('loading')}</span>
          </>
        )}
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || readOnly}
          title={readOnly ? t('you_dont_have_permissions_to_do_that') : undefined}
        >
          {t('confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PermissionsDialog
